import * as React from 'react';
import { getDrawersWithCounts, ApiDrawerWithCount } from '../api/drawerCounts';
import './ClassesPage.css';

export interface ClassesPageProps {
  onDrawerSelect: (id: number, name: string) => void;
}

export default function ClassesPage({ onDrawerSelect }: ClassesPageProps) {
  const [items, setItems] = React.useState<ApiDrawerWithCount[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const sortedItems = React.useMemo(() => {
    const getOrder = (d: ApiDrawerWithCount) => {
      const raw = (d as any).drawerOrder;
      if (raw === null || raw === undefined) return Number.MAX_SAFE_INTEGER;
      const n = Number(raw);
      return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
    };

    return [...items].sort((a, b) => {
      const ao = getOrder(a);
      const bo = getOrder(b);
      if (ao !== bo) return ao - bo;
      return (a.drawerName ?? '').localeCompare((b.drawerName ?? ''), undefined, { sensitivity: 'base' });
    });
  }, [items]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const list = await getDrawersWithCounts();
        if (!cancelled) setItems(list);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to load drawers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="classes-page">
      {error && <div className="classes-error">{error}</div>}
      {loading ? (
        <div>Loading...</div>
      ) : (
        <div className="classes-grid">
          {sortedItems.map(d => (
            <div
              key={d.id}
              className="class-panel"
              role="button"
              tabIndex={0}
              onClick={() => onDrawerSelect(d.id, d.drawerName)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onDrawerSelect(d.id, d.drawerName);
                }
              }}
              title="Select this drawer"
            >
              <div className="class-panel__name">{d.drawerName}</div>
              <div className="class-panel__count">
                <span className="class-panel__count-label">Documents:</span> {d.documentCount}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
